import { Fulfillment, FulfillmentItem } from '../../../__generated__/graphql';
import { useTimezones } from '../../../Common/hooks/useTimezones';
import { useQuery } from '@apollo/client';
import { graphql } from '../../../__generated__';
import { Badge, Button, Divider, HStack, Image, Pressable, Text, VStack } from 'native-base';
import React from 'react';
import currency from 'currency.js';
import {
  iconForShippingMethod,
  nameForShippingMethod,
} from '../../../PatientOrder/utils/shippingMethods';
import { nameForGateway } from '../../utils/utils';
import { OrderAge } from './OrderAge';

const OrderItem = ({ item }: { item: FulfillmentItem }) => {
  const { data } = useQuery(
    graphql(`
      query getMedicationName($id: ID) {
        medication(id: $id) {
          id
          name
          retail
          __typename
        }
      }
    `),
    {
      variables: { id: item?.ID },
      fetchPolicy: 'cache-first',
    },
  );

  return (
    <HStack space={2}>
      <Text flex={1}>
        {item?.QTY} x {data?.medication?.name}
      </Text>
      <Text>
        {currency(data?.medication?.retail ?? 0)
          .multiply(item?.QTY ?? 0)
          .format()}
      </Text>
    </HStack>
  );
};

export const OrderRow = ({
  order,
  onPress,
}: {
  order: Fulfillment;
  onPress?: (order: Fulfillment) => void;
}) => {
  const { convertUTCDateToUserISO } = useTimezones();

  return (
    <Pressable onPress={() => onPress(order)}>
      <HStack borderBottomColor={'gray.200'} borderBottomWidth={1}>
        <Text p={4} flex={1}>
          {convertUTCDateToUserISO(order?.createdAt)?.format('DD/MM/YYYY h:mm a')}
        </Text>

        <VStack p={4} flex={1}>
          <OrderAge createdAt={order?.createdAt} />
        </VStack>

        <VStack p={4} flex={1}>
          {!order?.completed && <Badge bg={'yellow.200'}>Pending</Badge>}

          {order?.completed && !order?.dispensed && (
            <Badge bg={'yellow.200'}>Paid - Pending Dispense</Badge>
          )}

          {order?.completed && order?.dispensed && order?.booked && (
            <Badge bg={'green.200'}>Shipped</Badge>
          )}
        </VStack>

        <Text p={4} flex={1}>
          {order?.customer?.name}
        </Text>

        <VStack p={4} space={1} flex={2}>
          {order?.items?.map((a) => (
            <OrderItem key={a?.ID} item={a as FulfillmentItem} />
          ))}

          <Divider />
          <HStack>
            <Text flex={1}>Shipping</Text>
            <Text>{currency(order?.shipping ?? 0).format()}</Text>
          </HStack>

          {order?.consults && order?.consults > 0 ? (
            <HStack>
              <Text flex={1}>Consult</Text>
              <Text>{currency(order?.consults ?? 0).format()}</Text>
            </HStack>
          ) : null}

          {order?.discounts && order?.discounts > 0 ? (
            <HStack>
              <Text flex={1}>Discounts</Text>
              <Text>{currency(order?.discounts ?? 0).format()}</Text>
            </HStack>
          ) : null}

          <HStack>
            <Text fontWeight={600} flex={1}>
              Total
            </Text>
            <Text fontWeight={600}>{currency(order?.total ?? 0).format()}</Text>
          </HStack>

          <Text>Paid via {order?.isPayInPerson ? "In Person (Cash)" : nameForGateway(order?.gateway)}</Text>
        </VStack>

        <HStack p={4} flex={1} space={2}>
          <Image source={iconForShippingMethod(order?.deliveryMethod)} w={6} h={6} />
          <Text>{nameForShippingMethod(order?.deliveryMethod)}</Text>
        </HStack>
      </HStack>
    </Pressable>
  );
};
